import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import AnimalAvatar from '../../components/AnimalAvatar';
import TransportChip from './TransportChip';
import './ShareSheet.css';

/**
 * F14.2 — Receiver consent share-sheet.
 *
 * Modal that gates an incoming file transfer. The receiver must Accept
 * before any chunk bytes flow. Decline (with optional session-block) tells
 * the sender to abort.
 *
 * Composes the existing Modal primitive:
 *  - Mobile (<700px) → bottom sheet (default Modal sheet variant + grabber)
 *  - Desktop (>=700px) → centered card
 *
 * Esc + scrim click route through Modal.onClose, which we wire to a plain
 * `Decline` (without `block`). The dedicated "Decline and block" link is the
 * only path that adds the sender to IdentityProvider.blocked for the session.
 *
 * The Accept button gets initial focus on mount so a keyboard user can
 * confirm with Enter immediately — the receiver-side intent is overwhelmingly
 * "yes, I expect this file."
 */

/** Pretty-print a byte count using binary thresholds with 1-decimal precision
 *  for KB/MB/GB and 0-decimal for bytes. We use base-10 prefixes (KB, MB, GB)
 *  matching what most OS UIs show — base-2 is more correct for memory but
 *  base-10 is what users see in iOS/Android/Windows file pickers. */
export function prettySize(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) {
    return '— B';
  }
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  // 1 decimal keeps "3.1 MB" / "52.0 KB" readable.
  const rounded = value >= 100 ? Math.round(value).toString() : value.toFixed(1);
  return `${rounded} ${units[i]}`;
}

/** Friendly MIME label. Covers the common Glacier file types; anything else
 *  falls back to the raw MIME so we never lie about the payload. */
export function prettyMime(mime) {
  if (typeof mime !== 'string' || !mime) return 'Unknown type';
  const lower = mime.toLowerCase();
  switch (lower) {
    case 'image/png': return 'PNG image';
    case 'image/jpeg':
    case 'image/jpg': return 'JPEG image';
    case 'image/gif': return 'GIF image';
    case 'image/webp': return 'WebP image';
    case 'image/svg+xml': return 'SVG image';
    case 'application/pdf': return 'PDF document';
    case 'application/zip': return 'ZIP archive';
    case 'application/x-tar': return 'TAR archive';
    case 'application/gzip': return 'GZIP archive';
    case 'application/json': return 'JSON file';
    case 'text/plain': return 'Text file';
    case 'text/markdown': return 'Markdown file';
    case 'text/csv': return 'CSV file';
    default: break;
  }
  if (lower.startsWith('image/')) return 'Image';
  if (lower.startsWith('video/')) return 'Video';
  if (lower.startsWith('audio/')) return 'Audio';
  if (lower.startsWith('text/')) return 'Text file';
  return mime;
}

/** Pick a file icon name for the given MIME type. Mirrors the design's
 *  three-bucket grouping (image / generic file / folder). */
export function iconForMime(mime) {
  if (typeof mime !== 'string' || !mime) return 'file';
  const lower = mime.toLowerCase();
  if (lower.startsWith('image/')) return 'image';
  if (lower === 'application/zip' || lower === 'application/x-tar' || lower === 'application/gzip') {
    return 'folder';
  }
  return 'file';
}

/** Icon background colorway for each file type. Mirrors the artboard 18
 *  purple for images; reuses Glacier's LAN green for archives/folders and a
 *  neutral blue for generic files. */
function iconPalette(iconName) {
  switch (iconName) {
    case 'image':
      return { bg: 'rgba(155, 107, 255, 0.18)', stroke: '#c9a8ff' };
    case 'folder':
      return { bg: 'rgba(63, 220, 138, 0.16)', stroke: '#b6ffd6' };
    case 'file':
    default:
      return { bg: 'rgba(143, 220, 255, 0.16)', stroke: '#8fdcff' };
  }
}

/** Mobile vs desktop variant for the Modal shell — matches PartyModal's
 *  read of `matchMedia('(min-width: 700px)')`. */
function useResponsiveVariant() {
  const get = () =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 700px)').matches
      : false;
  const [isDesktop, setIsDesktop] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 700px)');
    const handler = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isDesktop ? 'card' : 'sheet';
}

const ShareSheet = ({
  offer,
  senderName,
  senderAvatarSeed,
  onAccept,
  onDecline,
  onDeclineAndBlock,
}) => {
  const variant = useResponsiveVariant();
  const acceptRef = useRef(null);

  // Focus the Accept button on mount. Modal also moves focus to the first
  // focusable child after RAF, but ordering puts Decline first in the DOM —
  // we want Accept to win regardless. Run after Modal's focus pass.
  useEffect(() => {
    if (!offer) return undefined;
    const id = requestAnimationFrame(() => {
      // Second RAF so we run *after* Modal's own RAF-scheduled focus pass.
      requestAnimationFrame(() => {
        acceptRef.current?.focus();
      });
    });
    return () => cancelAnimationFrame(id);
  }, [offer]);

  const fileName = offer?.name ?? '';
  const fileSize = offer?.size ?? 0;
  const fileMime = offer?.mime ?? '';
  const via = offer?.via ?? 'lan';

  const iconName = useMemo(() => iconForMime(fileMime), [fileMime]);
  const palette = useMemo(() => iconPalette(iconName), [iconName]);

  const handleAccept = useCallback(() => {
    onAccept?.();
  }, [onAccept]);

  const handleDecline = useCallback(() => {
    onDecline?.();
  }, [onDecline]);

  const handleDeclineAndBlock = useCallback(() => {
    onDeclineAndBlock?.();
  }, [onDeclineAndBlock]);

  if (!offer) return null;

  const title = (
    <>
      <Icon name="upload" size={20} stroke="#fff" />
      <span>Receive file?</span>
    </>
  );

  return (
    <Modal
      isOpen
      onClose={handleDecline}
      variant={variant}
      title={title}
      ariaLabel="Receive file"
      className="share-sheet"
    >
      <p className="share-sheet__sub">
        <b>{senderName}</b> wants to send you a file. Bytes only flow if you accept.
      </p>

      <div
        className="file-card share-sheet__fc"
      >
        <div
          className="fc-ico"
          style={{
            background: palette.bg,
            border: `1px solid ${palette.stroke}55`,
          }}
        >
          <Icon name={iconName} size={20} stroke={palette.stroke} />
        </div>
        <div className="fc-meta">
          <b>{fileName}</b>
          <span className="sub">
            {prettySize(fileSize)} · {prettyMime(fileMime)}
          </span>
        </div>
      </div>

      <div className="share-sheet__sender">
        <AnimalAvatar seed={senderAvatarSeed} size={28} />
        <span>{senderName}</span>
      </div>

      <div className="share-sheet__transport">
        <TransportChip via={via} />
      </div>

      <div className="share-sheet__actions">
        <Button variant="ghost" onClick={handleDecline}>
          Decline
        </Button>
        <Button variant="primary" onClick={handleAccept} ref={acceptRef}>
          Accept
        </Button>
      </div>

      <button
        type="button"
        className="share-sheet__block-link"
        onClick={handleDeclineAndBlock}
      >
        Decline and block this user for this session
      </button>
    </Modal>
  );
};

export default ShareSheet;
